import { SagaIterator } from 'redux-saga';
import { all, call, put } from 'redux-saga/effects';
import * as _ from 'underscore';

import { CreateDatasourceExtractTaskActionService } from '~src/extract/actions/CreateDatasourceExtractTaskAction';
import { CreateWorkbookExtractTaskActionService } from '~src/extract/actions/CreateWorkbookExtractTaskAction';

import { Messages } from '../../../../codegen/Localize';
import { ExtractEncryptionState, IDataSourceBase, IDataSourceInfo } from '../../../../datasource/models/IDataSourceInfo';
import { getCommonDatasourceInfo } from '../../../../datasource/models/mappers/CommonDatasourceInfo';
import { DataSources } from '../../../../datasource/resources/Datasources';
import { IResult } from '../../../../httpClient/serverApi/types/IResponse';
import { DatasourceContentType, WorkbookContentType } from '../../../../ts/models/ContentTypes';
import { IContentInfo } from '../../../../ts/models/IContentInfo';
import { Set } from '../../../../ts/models/Set';
import { ICheckConnectionResult } from '../../../../ts/ServerApi/requests/CheckConnectionRequest';
import { requestFailed } from '../../../actions/RequestFailedAction';
import { EncryptionActionType } from '../../../actions/spaces/EncryptContentAction';
import { CreateExtractsAction } from '../../../actions/spaces/RunCreateExtractsAction';
import { encryptDataSources, encryptWorkbooks, processResult, showNotification } from '../content/EncryptContentSaga';
import { reloadSpace } from '../content/RefreshContentSagas';
import { getDatasourceInfos, replaceDatasourceIdWithConnectionIdForMixedContent } from '../SpacesSagaHelpers';

export function createExtracts(
  datasources: Set<IDataSourceBase>,
  encryptionState: ExtractEncryptionState,
): Promise<IResult> {
  // filter out the datasource which has extract or is ds.isFileBased or embedded ds refreence to published ds
  // for bridge based ds, it's only for published ds, we already disabled the button, it won't be here.
  const filteredDs = datasources.filter(ds =>!ds.hasExtracts && !ds.isFileBased && ds.connectionType  !== 'sqlproxy');
  if(filteredDs.size() < 1) {
    return null;
  }
  if(DataSources.isEmbedded(filteredDs.getByIndex(0))) {
    return CreateWorkbookExtractTaskActionService.createExtractsForWorkbooks(filteredDs.getByIndex(0).workbook, filteredDs, encryptionState);
  } else {
    return CreateDatasourceExtractTaskActionService.createExtractsForDatasources(filteredDs, encryptionState);
  }
}


export function* handleCreateExtractsAction(action: CreateExtractsAction): SagaIterator {
  let localizedErrorMessage = Messages.unexpectedError();
  try {
    const contentDictionaryByType = _.groupBy(action.selectedContent, c => c.contentType);
    const datasourceInfos: IDataSourceInfo[] = yield call(getDatasourceInfos, contentDictionaryByType['datasource']);
    const datasourceSet: Set<IDataSourceInfo> = Set.fromIdItemArray(datasourceInfos);
    // if datasoure has extract, we will do ecrypt or decrypt
    if(_.all(datasourceInfos, (ds: IDataSourceInfo) =>DataSources.isExtract(getCommonDatasourceInfo(ds)))) {
      const mutableContentCopy: IContentInfo[] = yield call(replaceDatasourceIdWithConnectionIdForMixedContent,datasourceInfos);
      const datasources = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === DatasourceContentType.name));
      const workbooks = Set.fromIdItemArray(mutableContentCopy.filter(i => i.contentType === WorkbookContentType.name));
      const encryptionActionType = action.encryptionState === ExtractEncryptionState.encrypted ?
               EncryptionActionType.encryptExtracts : EncryptionActionType.decryptExtracts;
      const [dataSourcesResult, workbooksResult] = (yield all([
        call(encryptDataSources, datasources, encryptionActionType),
        call(encryptWorkbooks, workbooks, encryptionActionType),
      ])) as any; // TFS 1116084 - need to upgrade redux-saga in order to remove 'as any'
      const result = processResult(dataSourcesResult, workbooksResult, null);
      yield call(showNotification, result, mutableContentCopy, encryptionActionType);
    } else {
      // filed based embedded datasource and embedded datasource which connected to public don't need check data connection
      const datasourceCheckSet = datasourceSet.filter((datasource) => datasource.dataConnectionsCount === 1 && datasource.testingConnectionAllowed
            && !(datasource.isFileBased || datasource.connectionType === 'sqlproxy' || datasource.connectionOAuth));

      if (datasourceCheckSet.size() > 0) {
        const results: ICheckConnectionResult[] = yield call(DataSources.checkConnections, datasourceCheckSet);
        if (!_.all(results, result=>result.succeeded)) {
          localizedErrorMessage = Messages.credentialMissedOrWrong();
          throw new Error(Messages.credentialMissedOrWrong());
        }
      }
      yield call(createExtracts, datasourceSet, action.encryptionState);
    }
    yield call(reloadSpace);
  } catch (e) {
    yield put(requestFailed(e, localizedErrorMessage));
  }
}
